import React, { useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import Button from "./Common/Button";
import Category from "./Category";
import { useSocketContext } from "./Common/Contexts";
import Countdowner from "./Countdowner";
import ping from "../Helpers/keepAliveHelper";
import useCountdown from "../Hooks/useCountdown";
import { useSocketListener } from "../Hooks/useSocketListener";
import { useTranslationContext } from "../Hooks/useTranslationContext";
import Game from "../Types/Game";

interface ActiveGameProps {
  gameId: number;
  game: Game;
}

const Overlay = styled.div<{ showCountdown: boolean }>`
  visibility: ${({ showCountdown }) => (showCountdown ? "hidden" : "visible")};
  position: relative;
`;

const Number = styled.div`
  font-weight: bold;
  font-size: 6em;
  padding: 0.2em;
  margin: 2rem;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StyledButton = styled(Button)<{ disableDone: boolean }>`
  visibility: ${({ disableDone }) => (disableDone ? "hidden" : "visible")};
  margin-top: 1.6em;
`;

const Letter = styled.div`
  font-weight: bold;
  font-size: 6em;
  padding: 0.2em;
  margin: 2rem;
`;

const ActiveGame = ({ game, gameId }: ActiveGameProps) => {
  const socket = useSocketContext();
  const { t } = useTranslationContext();
  const [answers, setAnswers] = useState<{ [category: string]: string }>({});
  const [showCountdown, setShowCountdown] = useState(true);
  const handleDone = useCallback(
    (automaticallyEnded: boolean = false) => {
      // Make a http request to the server to make sure it does not go to sleep
      ping();
      socket.emit("done", {
        gameId: gameId,
        playerId: automaticallyEnded ? undefined : socket.id,
      });
    },
    [gameId, socket]
  );
  const timeLeft = useCountdown(
    () => handleDone(true),
    game.timeEnd,
    game.timeLimitEnabled
  );
  const readyCountdownDone = useCallback(() => setShowCountdown(false), []);
  const readyCount = useCountdown(
    readyCountdownDone,
    useMemo(() => new Date().getTime() + 3000, []),
    true
  );
  useSocketListener("roundDone", socket, () =>
    socket.emit("answersToServer", {
      gameId,
      answers: game.categories.map((category) => ({
        category: category.name,
        answer: answers[category.name] ?? "",
      })),
    })
  );
  const handleAnswerChange = useCallback(
    (category: string, input: string) =>
      setAnswers({ ...answers, [category]: input }),
    [answers]
  );
  return (
    <>
      <Container>
        {showCountdown ? (
          <Number>{readyCount}</Number>
        ) : (
          <Letter data-test="letter">{game.letter}</Letter>
        )}
        <Overlay showCountdown={showCountdown}>
          {game.timeLimitEnabled && timeLeft && (
            <Countdowner
              timeLeft={timeLeft}
            >{`${timeLeft} ${t["seconds.left"]}`}</Countdowner>
          )}
          <div>
            {game.categories.map((c, i) => (
              <Category
                isShowing={showCountdown}
                index={i}
                key={c.name}
                onChange={handleAnswerChange}
                category={c.name}
                value={answers[c.name] ?? ""}
              />
            ))}
          </div>
          <StyledButton
            disableDone={showCountdown || game.disableDone}
            onClick={() => handleDone()}
          >
            {t.done}
          </StyledButton>
        </Overlay>
      </Container>
    </>
  );
};

export default ActiveGame;
